import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Building2, Upload, Check, AlertCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import { isValidPhone, normalizePhone, PHONE_ERROR } from '@/lib/phone';

// Organizer V1 — Phase 1 application form. Mirrors the DJ apply page. Logged-in only.
// Fresh submit OR edit + resubmit for a rejected / request_changes application (the
// backend updates the single record in place). Approved/pending users are sent to the
// dedicated /organizer-status page.
const IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Centered({ children }) { return <div className="max-w-2xl mx-auto px-4 py-16 text-center opacity-70">{children}</div>; }
function GateLogin() {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <p className="mb-4">Please log in to apply as an organizer.</p>
      <Link to="/login"><Button>Go to Login</Button></Link>
    </div>
  );
}

export default function OrganizerApply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authed] = useState(api.isAuthenticated());
  const [loading, setLoading] = useState(true);
  const [appState, setAppState] = useState(null);   // existing application (or null = none)
  const fileRef = useRef(null);

  // form
  const [orgName, setOrgName] = useState('');
  const [realName, setRealName] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [instagram, setInstagram] = useState('');
  const [website, setWebsite] = useState('');
  const [logoKey, setLogoKey] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [nameCheck, setNameCheck] = useState(null);   // {available, message}
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = async () => {
    if (!api.isAuthenticated()) { setLoading(false); return; }
    try {
      const a = await api.get('/users/me/organizer-application');
      setAppState(a);
      // Prefill for edit/resubmit (rejected / request_changes).
      if (a && ['rejected', 'request_changes'].includes(a.status)) {
        setOrgName(a.organization_name || '');
        setRealName(a.real_name || '');
        setDescription(a.description || '');
        setCity(a.city || '');
        setContactEmail(a.contact_email || '');
        setPhone(a.phone || '');
        setInstagram(a.instagram || '');
        setWebsite(a.website || '');
        setLogoKey(a.logo_key || null);
        if (a.logo_url) {
          api.fetchAuthedBlobUrl(a.logo_url).then(setLogoPreview).catch(() => {});
        }
      }
    } catch (_) { setAppState(null); }   // 404 => no application
    finally { setLoading(false); }
  };
  useEffect(() => { loadStatus(); }, []);

  // Organization-name availability (debounced).
  useEffect(() => {
    const nm = orgName.trim();
    if (!nm) { setNameCheck(null); return; }
    const t = setTimeout(() => {
      api.get(`/organizers/name-availability?name=${encodeURIComponent(nm)}`)
        .then((r) => setNameCheck(r)).catch(() => setNameCheck(null));
    }, 400);
    return () => clearTimeout(t);
  }, [orgName]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!IMG_TYPES.includes(f.type)) { toast.error('Use a JPG, PNG, or WebP image.'); return; }
    if (f.size > MAX_BYTES) { toast.error('Image too large (max 5 MB).'); return; }
    setLogoPreview(URL.createObjectURL(f));   // local preview (pending logos aren't public)
    setUploading(true);
    try {
      const res = await api.uploadOrganizerImage(f);
      setLogoKey(res.image_key);
      toast.success('Logo uploaded');
    } catch (err) {
      setLogoKey(null);
      toast.error(err.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const validate = () => {
    if (!orgName.trim()) return 'Organization name is required.';
    if (nameCheck && nameCheck.available === false) return 'This organization name is already in use.';
    if (!realName.trim()) return 'Your real name is required.';
    if (!description.trim()) return 'A description is required.';
    if (!city.trim()) return 'City is required.';
    if (!contactEmail.trim()) return 'Contact email is required.';
    if (!EMAIL_RE.test(contactEmail.trim())) return 'Enter a valid contact email.';
    if (!phone.trim()) return 'Phone number is required.';
    if (!isValidPhone(phone)) return PHONE_ERROR;
    if (instagram.trim() && !/^https:\/\/.+/i.test(instagram.trim())) return 'Instagram must be a full https:// URL.';
    if (website.trim() && !/^https?:\/\/.+/i.test(website.trim())) return 'Website must start with http:// or https://';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        organization_name: orgName.trim(),
        real_name: realName.trim(),
        description: description.trim(),
        city: city.trim(),
        contact_email: contactEmail.trim(),
        phone: normalizePhone(phone),
        instagram: instagram.trim() || null,
        website: website.trim() || null,
        logo_key: logoKey || null,
      };
      await api.post('/organizers/apply', payload);
      toast.success('Application submitted — pending review.');
      navigate('/organizer-status');
    } catch (err2) {
      toast.error(err2.message || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (!authed) return <GateLogin />;
  if (loading) return <Centered>Loading…</Centered>;

  // Status routing: pending/approved/suspended users view their status page.
  const st = appState?.status;
  if (st === 'pending' || st === 'approved' || st === 'suspended') return <Navigate to="/organizer-status" replace />;

  const isResubmit = ['rejected', 'request_changes'].includes(st);

  return (
    <div className="max-w-2xl mx-auto px-4 pt-20 pb-24 md:pb-8" data-testid="organizer-apply-page">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-2"><Building2 className="h-6 w-6" /> Apply as Organizer</h1>
      <p className="opacity-70 mb-6">Approved organizers get a verified public profile. Applications are reviewed by our team.</p>

      {isResubmit && (
        <div className="mb-6 rounded-md border border-border/60 bg-muted/40 p-3 text-sm flex gap-2" data-testid="organizer-apply-resubmit-note">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            {st === 'request_changes'
              ? 'The reviewer asked for changes. Update your details below and resubmit — this edits your existing application.'
              : 'Your application was not approved. You can edit the details below and resubmit the same application.'}
            {appState?.rejection_reason && <span className="block mt-1 opacity-80">Reason: {appState.rejection_reason}</span>}
            {appState?.request_changes_message && <span className="block mt-1 opacity-80">Note: {appState.request_changes_message}</span>}
          </span>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Organizer application</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="orgn">Organization name *</Label>
              <Input id="orgn" value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
              {nameCheck && (
                <p className={`text-xs mt-1 ${nameCheck.available ? 'text-green-600' : 'text-red-600'}`}>{nameCheck.message}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label htmlFor="rn">Your real name *</Label><Input id="rn" value={realName} onChange={(e) => setRealName(e.target.value)} required /></div>
              <div><Label htmlFor="city">City *</Label><Input id="city" value={city} onChange={(e) => setCity(e.target.value)} required /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label htmlFor="ce">Contact email *</Label><Input id="ce" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="bookings@…" required /></div>
              <div>
                <Label htmlFor="phone">Phone number *</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} required />
              </div>
            </div>
            <div>
              <Label htmlFor="desc">Description *</Label>
              <Textarea id="desc" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell us about your organization and the events you run." />
            </div>
            <div>
              <Label>Logo / profile image (optional)</Label>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" />
              <div className="flex items-center gap-3 mt-1">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  <Upload className="h-4 w-4 mr-2" />{uploading ? 'Uploading…' : 'Choose image'}
                </Button>
                {logoPreview && <img src={logoPreview} alt="logo preview" className="h-14 w-14 rounded-md object-cover" />}
                {logoKey && <Check className="h-4 w-4 text-green-600" />}
              </div>
              <p className="text-xs opacity-60 mt-1">JPG, PNG or WebP, max 5 MB. Cropped to a square. Not shown publicly until approved.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label htmlFor="ig">Instagram (optional)</Label><Input id="ig" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="https://instagram.com/…" /></div>
              <div><Label htmlFor="web">Website (optional)</Label><Input id="web" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" /></div>
            </div>

            <div className="pt-2 flex justify-end">
              <Button type="submit" disabled={submitting || uploading}>{submitting ? 'Submitting…' : (isResubmit ? 'Resubmit application' : 'Submit application')}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
